import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ChevronLeft,
  CreditCard,
  Check,
  X,
  Calendar,
  Users,
  ShoppingCart,
  Target,
  Building2,
  Zap,
  Crown,
  RefreshCw,
  AlertCircle,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// New subscription plans (matching BillingPage.js)
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: '$',
    billing: 'monthly',
    icon: Sparkles,
    color: 'gray',
    features: [
      '100 orders/month',
      '100 leads/month',
      '2 team members',
      'Basic features'
    ],
    limits: {
      users: 2,
      orders: 100,
      leads: 100,
      integrations: 1
    }
  },
  {
    id: 'standard',
    name: 'Standard',
    price: 29,
    currency: '$',
    billing: 'monthly',
    icon: Zap,
    color: 'blue',
    features: [
      '1,000 orders/month',
      '1,000 leads/month',
      '5 team members',
      'All integrations',
      'Email support'
    ],
    limits: {
      users: 5,
      orders: 1000,
      leads: 1000,
      integrations: -1
    }
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 99,
    currency: '$',
    billing: 'monthly',
    icon: Crown,
    color: 'purple',
    popular: true,
    features: [
      '5,000 orders/month',
      '5,000 leads/month',
      '25 team members',
      'All integrations',
      'WhatsApp Business',
      'Priority support'
    ],
    limits: {
      users: 25,
      orders: 5000,
      leads: 5000,
      integrations: -1
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299,
    currency: '$',
    billing: 'monthly',
    icon: Building2,
    color: 'gold',
    features: [
      'Unlimited orders',
      'Unlimited leads',
      'Unlimited team members',
      'All integrations',
      'Custom branding',
      'Dedicated support',
      'SLA Guarantee'
    ],
    limits: {
      users: -1,
      orders: -1,
      leads: -1,
      integrations: -1
    }
  }
];

const CompanyBillingPage = () => {
  const { companyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [company, setCompany] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (user && !user.is_super_admin) {
      toast.error('Access denied. Super Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchCompanyData();
  }, [user, companyId, navigate]);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [companyRes, usageRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/companies/${companyId}`, { headers }),
        axios.get(`${API_BASE_URL}/companies/${companyId}/usage`, { headers })
      ]);
      
      setCompany(companyRes.data);
      setUsage(usageRes.data.usage);
    } catch (error) {
      console.error('Failed to fetch company data:', error);
      toast.error('Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlan = () => {
    const planId = company?.subscription?.plan || 'enterprise';
    return PLANS.find(p => p.id === planId) || PLANS[2];
  };

  const handleUpgrade = async (plan) => {
    setSelectedPlan(plan);
    setShowUpgradeDialog(true);
  };

  const confirmUpgrade = async () => {
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/companies/${companyId}`,
        {
          subscription: {
            plan: selectedPlan.id,
            billing_cycle: 'monthly',
            status: 'active',
            usage_limits: selectedPlan.limits
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Upgraded to ${selectedPlan.name} plan!`);
      setShowUpgradeDialog(false);
      fetchCompanyData();
    } catch (error) {
      toast.error('Failed to update subscription');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getUsagePercent = (current, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((current / limit) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  const currentPlan = getCurrentPlan();

  return (
    <div className="space-y-6 p-6" data-testid="company-billing">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/super-admin/companies/${companyId}/settings`)}
          className="text-wt-text-secondary hover:text-wt-text-primary"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">{company?.name}</h1>
          <p className="text-wt-text-secondary">Billing & Subscription</p>
        </div>
      </div>

      {/* Current Plan */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-wt-text-primary flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-wt-gold" />
                Current Subscription
              </CardTitle>
              <CardDescription className="text-wt-text-secondary">
                Manage your subscription and billing
              </CardDescription>
            </div>
            <Badge className={`bg-${currentPlan.color}-500/10 text-${currentPlan.color}-400 border-${currentPlan.color}-500/20`}>
              {currentPlan.name}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-wt-bg p-4 rounded-lg">
              <p className="text-sm text-wt-text-secondary">Monthly Price</p>
              <p className="text-2xl font-bold text-wt-text-primary">
                {formatCurrency(currentPlan.price)}
                <span className="text-sm font-normal text-wt-text-secondary">/month</span>
              </p>
            </div>
            <div className="bg-wt-bg p-4 rounded-lg">
              <p className="text-sm text-wt-text-secondary">Billing Status</p>
              <Badge className="bg-green-500/10 text-green-400 mt-1">
                {company?.subscription?.status || 'Active'}
              </Badge>
            </div>
            <div className="bg-wt-bg p-4 rounded-lg">
              <p className="text-sm text-wt-text-secondary">Next Billing Date</p>
              <p className="text-lg font-medium text-wt-text-primary flex items-center gap-2">
                <Calendar className="h-4 w-4 text-wt-text-secondary" />
                {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage */}
      <Card className="bg-wt-surface border-wt-border">
        <CardHeader>
          <CardTitle className="text-wt-text-primary">Usage This Month</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-wt-bg rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-400" />
                  <span className="text-sm text-wt-text-secondary">Users</span>
                </div>
                <span className="text-sm text-wt-text-primary">
                  {usage?.users || 0} / {currentPlan.limits.users === -1 ? '∞' : currentPlan.limits.users}
                </span>
              </div>
              <Progress 
                value={getUsagePercent(usage?.users || 0, currentPlan.limits.users)} 
                className="h-2 bg-wt-border"
              />
            </div>
            
            <div className="p-4 bg-wt-bg rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-green-400" />
                  <span className="text-sm text-wt-text-secondary">Orders</span>
                </div>
                <span className="text-sm text-wt-text-primary">
                  {usage?.orders || 0} / {currentPlan.limits.orders === -1 ? '∞' : currentPlan.limits.orders}
                </span>
              </div>
              <Progress 
                value={getUsagePercent(usage?.orders || 0, currentPlan.limits.orders)} 
                className="h-2 bg-wt-border"
              />
            </div>
            
            <div className="p-4 bg-wt-bg rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-orange-400" />
                  <span className="text-sm text-wt-text-secondary">Leads</span>
                </div>
                <span className="text-sm text-wt-text-primary">
                  {usage?.leads || 0} / {currentPlan.limits.leads === -1 ? '∞' : currentPlan.limits.leads}
                </span>
              </div>
              <Progress 
                value={getUsagePercent(usage?.leads || 0, currentPlan.limits.leads)} 
                className="h-2 bg-wt-border"
              />
            </div>
            
            <div className="p-4 bg-wt-bg rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-purple-400" />
                  <span className="text-sm text-wt-text-secondary">Customers</span>
                </div>
                <span className="text-sm text-wt-text-primary">{usage?.customers || 0}</span>
              </div>
              <Progress value={0} className="h-2 bg-wt-border" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans */}
      <div>
        <h2 className="text-lg font-semibold text-wt-text-primary mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLANS.map((plan) => (
            <Card 
              key={plan.id}
              className={`bg-wt-surface border-wt-border relative ${
                plan.popular ? 'border-wt-gold' : ''
              } ${currentPlan.id === plan.id ? 'ring-2 ring-wt-gold' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-wt-gold text-black">Most Popular</Badge>
                </div>
              )}
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg bg-${plan.color}-500/10`}>
                    <plan.icon className={`h-5 w-5 text-${plan.color}-400`} />
                  </div>
                  <CardTitle className="text-wt-text-primary">{plan.name}</CardTitle>
                </div>
                <div className="mt-4">
                  <span className="text-3xl font-bold text-wt-text-primary">
                    {formatCurrency(plan.price)}
                  </span>
                  <span className="text-wt-text-secondary">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-wt-text-secondary">
                      <Check className="h-4 w-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                {currentPlan.id === plan.id ? (
                  <Button disabled className="w-full bg-wt-surface-hover text-wt-text-secondary">
                    Current Plan
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleUpgrade(plan)}
                    className={`w-full ${
                      PLANS.indexOf(plan) > PLANS.indexOf(currentPlan)
                        ? 'bg-wt-gold hover:bg-wt-gold-dark text-black'
                        : 'bg-wt-surface-hover text-wt-text-primary hover:bg-wt-border'
                    }`}
                  >
                    {PLANS.indexOf(plan) > PLANS.indexOf(currentPlan) ? 'Upgrade' : 'Downgrade'}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              {selectedPlan && PLANS.indexOf(selectedPlan) > PLANS.indexOf(currentPlan) 
                ? 'Upgrade' : 'Change'} to {selectedPlan?.name}
            </DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              This will update the subscription plan for {company?.name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {selectedPlan && (
              <div className="bg-wt-bg p-4 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-wt-text-secondary">New monthly price:</span>
                  <span className="text-xl font-bold text-wt-text-primary">
                    {formatCurrency(selectedPlan.price)}
                  </span>
                </div>
                <div className="text-sm text-wt-text-muted">
                  {PLANS.indexOf(selectedPlan) > PLANS.indexOf(currentPlan) ? (
                    <p>The price difference will be prorated for the current billing period.</p>
                  ) : (
                    <p>Changes will take effect at the start of the next billing cycle.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowUpgradeDialog(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmUpgrade}
              disabled={processing}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              {processing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyBillingPage;
